import { useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Message } from "../../interfaces/chat.interface";
import { ChatContext } from "../../store/chat.store";
import {
  CHAT_CONFIG,
  CHAT_SUGGESTIONS,
  ConversationType,
} from "../../constants/chat.constant";
import FullScreenLoader from "../../components/loader/fullscreen-loader";
import UpcomingChangesModal from "@/components/popup/upcoming-changes-modal";
import PolicyAssistantReminderModal from "@/components/popup/policy-assistant-reminder-modal";
import { UPCOMING_CHANGES } from "@/constants/consent.constants";
import { useCitation } from "@/hooks/use-citation";
import { Citation } from "@/constants/citation.constant";
import { useChatState } from "../../hooks/useChatState";
import { useChatActions } from "../../hooks/useChatActions";
import { useChatEffects } from "../../hooks/useChatEffects";
import { ChatLayout } from "../../components/chat/ChatLayout";
import useChatApiService from "../../services/chat.service";
import toast from "react-hot-toast";
import useAuthService from "@/services/auth.service";
import { ChatInputRef } from "../../components/chat/chat-input";
import { useIsMobile } from "@/hooks/use-mobile";

declare global {
  interface Window {
    openCitationDetails?: (citation: Citation) => void;
  }
}

export default function GptChat() {
  const { type, conversationId } = useParams<{
    type: string;
    conversationId?: string;
  }>();

  const [conversationType, setConversationType] = useState<ConversationType>(
    ConversationType.CHOP_GPT
  );

  const [isSensitive, setIsSensitive] = useState<boolean>(false);
  const [showPolicyReminder, setShowPolicyReminder] = useState(false);
  const chatInputRef = useRef<ChatInputRef>(null);
  const isMobile = useIsMobile();

  const { getUserAccessToken } = useAuthService();

  const suggestions =
    conversationType === ConversationType.CHOP_GPT
      ? CHAT_SUGGESTIONS.GPT
      : CHAT_SUGGESTIONS.POLICY_ASSISTANT;

  const mobileSuggestions =
    conversationType === ConversationType.CHOP_GPT
      ? CHAT_SUGGESTIONS.MOBILE_GPT
      : CHAT_SUGGESTIONS.POLICY_ASSISTANT;

  const { state, dispatch } = useContext(ChatContext);

  const chatState = useChatState();

  const navigate = useNavigate();

  const {
    sourcesOpen,
    sources,
    openSources,
    closeSources,
    openCitation,
    citationDetailsOpen,
    citationDetails,
    isLoading,
    selectedCitationId,
    openCitationDetails,
    closeCitationDetails,
  } = useCitation();

  const { getChatConversation } = useChatApiService();

  const { chatFolderDetails, refetchFolders, isChatHistoryLoading } =
    useChatEffects(
      chatState,
      state,
      dispatch,
      conversationType,
      setConversationType,
      openCitationDetails,
      closeSources
    );

  const handleCloseSources = () => {
    chatState.setActiveMessageIndex(null);
    closeSources();
  };

  const chatActions = useChatActions(
    chatState,
    state,
    dispatch,
    conversationType,
    setConversationType,
    refetchFolders,
    handleCloseSources,
    isSensitive
  );

  // Content persistence effect
  useEffect(() => {
    closeSources();
    const conversationKey = state.selectedConversation?.session_id || "new";
    const content = chatState.getContentForConversation(conversationKey);
    chatState.setContent(content);
  }, [state.selectedConversation?.session_id, closeSources]);

  // Conversation loading effect
  useEffect(() => {
    const selectedConversationId =
      state.selectedConversation?.session_id?.trim();

    if (!selectedConversationId) {
      chatState.setActiveConversation([]);
      return;
    }

    const isExisting = state.conversationStatusMap.has(selectedConversationId)
      ? state.conversationStatusMap.get(selectedConversationId)
      : true;

    if (isExisting) {
      dispatch({ field: "isLoading", value: true });
      getChatConversation(selectedConversationId, conversationType)
        .then((response) => {
          if (
            state.selectedConversation?.session_id !== selectedConversationId
          ) {
            dispatch({ field: "isLoading", value: false });
            return;
          }

          const parsedConversation: Array<Message> = [];

          if (response && response.data) {
            const chatSessionData = response.data;
            for (const item of chatSessionData.messages) {
              const message = item.message;
              parsedConversation.push({
                id: item.id,
                sessionId: chatSessionData.session_id,
                role: message.role,
                content: message.content,
                reasoning: message.reasoning,
                citations: item.citations || [],
                uploads: item.uploads || [],
                feedback: item.feedback,
                conversationType: chatSessionData.conversation_type,
              });
            }

            if (state.selectedConversation?.session_id === chatSessionData.session_id) {
              dispatch({
                field: "selectedConversation",
                value: {
                  ...state.selectedConversation,
                  sensitive: chatSessionData.sensitive || false,
                },
              });
            }
          }
          chatState.setActiveConversation(parsedConversation);
          dispatch({ field: "isLoading", value: false });
        })
        .catch((err) => {
          console.error(
            "ERROR: Error occurred while getting chat conversation details",
            err
          );
          if (
            state.selectedConversation?.session_id === selectedConversationId
          ) {
            chatState.setActiveConversation([]);
            dispatch({ field: "isLoading", value: false });
            toast.error(err && err.error ? err.error : "Something went wrong.");
          }
        });
    } else {
      chatState.setActiveConversation(
        chatState.activeConversation && chatState.activeConversation.length > 0
          ? chatState.activeConversation
          : []
      );
    }
  }, [
    state.selectedConversation?.session_id,
    conversationType
  ]);

  // Streaming conversation sync effect
  useEffect(() => {
    if (
      !chatState.isResponseGenerating &&
      chatState.streamingConversation &&
      chatState.streamingConversationId &&
      chatState.streamingConversationId ===
        state.selectedConversation?.session_id
    ) {
      chatState.setActiveConversation(chatState.streamingConversation);
      chatState.setStreamingConversation(null);
      chatState.setStreamingConversationId(null);
    }
  }, [
    chatState.isResponseGenerating,
    chatState.streamingConversation,
    chatState.streamingConversationId,
    state.selectedConversation?.session_id,
  ]);

  // Computed values
  const displayConversation = chatState.getDisplayConversation(
    state.selectedConversation?.session_id
  );
  const hasMessages = Boolean(
    displayConversation && displayConversation.length > 0
  );

  useEffect(() => {
    setShowPolicyReminder(
      conversationType === ConversationType.POLICY_ASSISTANT
    );
    chatState.setModelEffort(
      conversationType === ConversationType.POLICY_ASSISTANT
        ? String(CHAT_CONFIG.REASONING_EFFORT.MEDIUM)
        : String(CHAT_CONFIG.REASONING_EFFORT.DEFAULT)
    );
  }, [conversationType]);

  useEffect(() => {
    if (state.selectedConversation) {
      setIsSensitive(state.selectedConversation.sensitive || false);
    } else {
      setIsSensitive(false);
    }
  }, [state.selectedConversation?.session_id, state.selectedConversation?.sensitive]);

  useEffect(() => {
    if (isMobile) return;
    const attemptFocus = (retries = 15) => {
      if (chatInputRef.current) {
        chatInputRef.current.focus();
      } else if (retries > 0) {
        setTimeout(() => attemptFocus(retries - 1), 150);
      }
    };
    
    let timeoutId: NodeJS.Timeout;

    const rafId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => {
        attemptFocus();
      }, 300);
    });
    
    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    state.selectedConversation,
    state.selectedConversation?.session_id,
    conversationId,
    conversationType,
    hasMessages,
    isMobile
  ]);

  const handleToggleSensitive = () => {
    if (!hasMessages) {
      setIsSensitive(!isSensitive);
    }
  };

  const isSensitiveDisabled = hasMessages;

  const handleOpenSources = (sources: Citation[], messageIndex: number) => {
    chatState.setActiveMessageIndex(messageIndex);
    openSources(sources);
  };

  const wasGeneratingRef = useRef(false);
  useEffect(() => {
    const wasGenerating = wasGeneratingRef.current;
    wasGeneratingRef.current = chatState.isResponseGenerating;
    if (!wasGenerating || chatState.isResponseGenerating) return;
    if (!displayConversation) return;

    for (let i = displayConversation.length - 1; i >= 0; i--) {
      const msg = displayConversation[i];
      if (msg.role !== "assistant") continue;
      if (msg.citations && msg.citations.length > 0) {
        const sources = msg.citations.map((citation) => ({
          name: citation.source_file,
          content: citation.content_snippet,
          url: citation.source_file,
          parentFolder: citation.source_file,
          ...citation,
        })) as Citation[];
        handleOpenSources(sources, i);
      }
      break;
    }
  }, [chatState.isResponseGenerating, displayConversation]);

  return (
    <div className="chat-wrapper h-full bg-[#fefefe]">
        <ChatLayout
          conversationType={conversationType}
          displayConversation={displayConversation}
          isResponseGenerating={chatState.isResponseGenerating}
          isLoading={chatState.isLoading}
          streamingConversationId={chatState.streamingConversationId}
          selectedConversationId={state.selectedConversation?.session_id}
          sourcesOpen={sourcesOpen}
          activeMessageIndex={chatState.activeMessageIndex}
          content={chatState.content}
          selectedModel={chatState.selectedModel}
          modelTemperature={parseFloat(chatState.modelTemperature)}
          modelEffort={parseInt(chatState.modelEffort)}
          hasMessages={hasMessages}
          showUpcoming={chatState.showUpcoming}
          isChatHistoryLoading={isChatHistoryLoading}
          isCitationLoading={isLoading}
          selectedCitationId={selectedCitationId}
          onConversationTypeChange={chatActions.handleConversationTypeChange}
          onOpenSources={handleOpenSources}
          onCloseSources={handleCloseSources}
          onOpenCitationDetails={openCitationDetails}
          onSend={chatActions.handleSend}
          onStartTyping={() => chatState.setIsTyping(true)}
          onStopTyping={() => chatState.setIsTyping(false)}
          onInputChange={chatActions.handleChange}
          onKeyDown={chatActions.handleKeyDown}
          onModelChange={chatActions.handleModelChange}
          onTemperatureChange={chatActions.handleTemperatureChange}
          onReasoningEffortChange={chatActions.handleReasoningEffortChange}
          onNewChat={() => chatState.setContent("")}
          setShowUpcoming={chatState.setShowUpcoming}
          refetchFolders={refetchFolders}
          suggestions={suggestions}
          mobileSuggestions={mobileSuggestions}
          sources={sources}
          citationDetails={citationDetails}
          citationDetailsOpen={citationDetailsOpen}
          onCloseCitationDetails={closeCitationDetails}
          selectedConversation={state.selectedConversation}
          activeConversation={chatState.activeConversation}
          getFilesForConversation={chatState.getFilesForConversation}
          updateFilesForConversation={chatState.updateFilesForConversation}
          isSensitive={isSensitive}
          onToggleSensitive={handleToggleSensitive}
          isSensitiveDisabled={isSensitiveDisabled}
          onCancelStream={chatActions.cancelStream}
          chatInputRef={chatInputRef}
        />

      {(state.isLoading || isChatHistoryLoading) && <FullScreenLoader />}
      <UpcomingChangesModal
        open={chatState.showUpcoming}
        onClose={() => chatState.setShowUpcoming(false)}
        title={UPCOMING_CHANGES.title}
        intro={UPCOMING_CHANGES.intro}
        items={UPCOMING_CHANGES.items}
      />
      <PolicyAssistantReminderModal
        open={showPolicyReminder}
        onClose={() => setShowPolicyReminder(false)}
      />
    </div>
  );
}
